import React, { FC, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useActiveClientContext } from 'store/active-client';
import { useLanguageContext } from 'store/language';
import API from 'core/Api';
import { buildClientEntity } from 'utils/helpers/client';
import { createErrorFromResponse } from 'core/Error';
import { formatDate } from 'utils/helpers/formatters';
import Form from 'components/form/Form';
import FormGroup from 'components/form/FormGroup';
import CreateEntity from 'components/crud/CreateEntity';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Button from 'components/Button';
import styled from 'styled-components';
import { useModal } from 'store/modal';

interface ISelectOptionProps {
  label: string;
  value: string | number | boolean;
  groups?: number[];
}

interface IInputs {
  user: ISelectOptionProps;
  date: Date | null;
  timeFrom: Date | null;
  timeTo: Date | null;
  comment?: string;
  lunchBreak?: number;
}

interface FormattedHourlyAbsence {
  user_id: number;
  datetime_from: string | null;
  datetime_to: string | null;
  lunch_break: number;
  comment?: string;
}

const CreateHourlyAbsencesForm: FC = () => {
  const { t } = useTranslation();
  const { client } = useActiveClientContext();
  const { language } = useLanguageContext();
  const methods = useForm<IInputs>({
    defaultValues: {
      user: {},
      date: null,
      timeFrom: null,
      timeTo: null,
      lunchBreak: 0,
    },
  });
  const { showModal } = useModal();

  const {
    isLoading: isLoadingDropdowns,
    error: errorDropdowns,
    data: dropdowns,
  } = useQuery<{ users: ISelectOptionProps[] }, Error>(
    [buildClientEntity('hourly-absences-dropdowns', client)],
    async (): Promise<{ users: ISelectOptionProps[] }> => {
      const res = await API.get('hourly-absences/dropdown-options?all-users=true');
      const userData: ISelectOptionProps[] = res.data?.[language || 'bg'].user || [];
      const groups = res.data?.[language || 'bg'].group || [];

      interface IGroup {
        value: number;
        label: string;
      }

      const groupLookup = groups.reduce((acc: Record<number, string>, group: IGroup) => {
        acc[group.value] = group.label;
        return acc;
      }, {} as Record<number, string>);

      const labelCounts: Record<string, number> = userData.reduce(
        (acc: Record<string, number>, user: ISelectOptionProps) => {
          acc[user.label] = (acc[user.label] || 0) + 1;
          return acc;
        },
        {}
      );

      const updatedUsers: ISelectOptionProps[] = userData.map((user: ISelectOptionProps) => {
        if (labelCounts[user.label] > 1 && user.groups && user.groups.length > 0) {
          const groupNames = user.groups
            .map(groupId => groupLookup[groupId] || '')
            .filter(name => name !== '')
            .join(', ');

          return {
            ...user,
            label: `${user.label} - ${groupNames}`,
          };
        }
        return user;
      });

      return { users: updatedUsers };
    },
    { cacheTime: 0 }
  );

  const FormDropdowns = useMemo(
    () => ({
      users: dropdowns?.users || [],
    }),
    [dropdowns]
  );

  const handleSubmit = async (data: IInputs, create: any) => {
    showModal({
      type: 'confirm',
      heading: t('common.confirm'),
      content: t('hourlyAbsences.confirmationMessage'),
      onConfirm: async () => {
        try {
          const datetimeFrom =
            data.date && data.timeFrom
              ? new Date(data.date).setHours(data.timeFrom.getHours(), data.timeFrom.getMinutes())
              : null;

          const datetimeTo =
            data.date && data.timeTo
              ? new Date(data.date).setHours(data.timeTo.getHours(), data.timeTo.getMinutes())
              : null;

          const formattedData: FormattedHourlyAbsence = {
            user_id: data.user.value as number,
            datetime_from: datetimeFrom ? formatDate(new Date(datetimeFrom), 'Y-mm-dd hh:ii:ss') : null,
            datetime_to: datetimeTo ? formatDate(new Date(datetimeTo), 'Y-mm-dd hh:ii:ss') : null,
            lunch_break: data.lunchBreak || 0,
          };

          if (data.comment && data.comment.trim() !== '') {
            formattedData.comment = data.comment;
          }

          await create(formattedData);
        } catch (error) {
          showModal({
            type: 'error',
            content: createErrorFromResponse(error).message,
          });
        }
      },
      confirmLabel: t('hourlyAbsences.confirm'),
    });
  };

  const LunchBreakSelector: FC = () => {
    const { setValue, watch } = methods;

    const selected = watch('lunchBreak', 0);

    const options = [
      { label: t('hourlyAbsences.noLunchBreak'), value: 0 },
      { label: t('hourlyAbsences.lunchbreakHalf'), value: 0.5 },
      { label: t('hourlyAbsences.lunchbreakOne'), value: 1 },
    ];

    return (
      <div className="mb-5">
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label id="lunch-break-label">{t('hourlyAbsences.lunchbreak')}</label>
        <SwitchButtons
          role="group"
          aria-labelledby="lunch-break-label"
        >
          {options.map(option => (
            <Button
              key={option.value}
              type="button"
              btnType={selected === option.value ? 'primary' : 'secondary'}
              size="sm"
              aria-pressed={selected === option.value}
              onClick={() => setValue('lunchBreak', option.value)}
            >
              {option.label}
            </Button>
          ))}
        </SwitchButtons>
      </div>
    );
  };

  return (
    <StyledFormGroup>
      <Row>
        <Col className="col-sm-6">
          <CreateEntity
            entity="hourly-absences"
            methods={methods}
            queryKey={buildClientEntity('hourly-absences', client)}
            onSuccess={() => {
              showModal({
                type: 'success',
                content: t('hourlyAbsences.createSuccess'),
              });
              methods.reset();
            }}
          >
            {(create: any) => (
              <Form
                title={t('hourlyAbsences.create')}
                methods={methods}
                btnLabel={t('hourlyAbsences.create')}
                primaryActionBtnType="primary"
                isUploading={isLoadingDropdowns}
                onSubmit={(data: IInputs) => handleSubmit(data, create)}
              >
                <Row>
                  <Col md={{ span: 6, offset: 0 }}>
                    <FormGroup
                      controlId="formGroupUser"
                      label={t('hourlyAbsences.employee')}
                      type="select"
                      as="select"
                      name="user"
                      selectOptions={FormDropdowns.users}
                      isRequired
                    />
                  </Col>
                  <Col md={{ span: 6, offset: 0 }}>
                    <FormGroup
                      controlId="formGroupDate"
                      label={t('hourlyAbsences.inputs.date')}
                      type="datepicker"
                      name="date"
                      isRequired
                      dateFormat="dd.MM.yyyy"
                      showYearDropdown
                      showMonthDropdown
                      maxDate={new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)}
                    />
                  </Col>
                </Row>
                <Row className="mt-1">
                  <Col md={{ span: 6, offset: 0 }}>
                    <FormGroup
                      controlId="formGroupTimeFrom"
                      label={t('hourlyAbsences.inputs.timeFrom')}
                      type="datepicker"
                      name="timeFrom"
                      showTimeSelect
                      showTimeSelectOnly
                      isRequired
                      dateFormat="HH:mm"
                      onChange={(date: Date) => methods.clearErrors()}
                    />
                  </Col>
                  <Col md={{ span: 6, offset: 0 }}>
                    <FormGroup
                      controlId="formGroupTimeTo"
                      label={t('hourlyAbsences.inputs.timeTo')}
                      type="datepicker"
                      name="timeTo"
                      showTimeSelect
                      showTimeSelectOnly
                      isRequired
                      dateFormat="HH:mm"
                      onChange={(date: Date) => methods.clearErrors()}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <FormGroup
                      controlId="formGroupComment"
                      label={t('hourlyAbsences.comment')}
                      type="text"
                      name="comment"
                      maxLength={255}
                      hint={t('hourlyAbsences.commentInfoManual')}
                    />
                  </Col>
                </Row>
                <LunchBreakSelector />
              </Form>
            )}
          </CreateEntity>
        </Col>
      </Row>
    </StyledFormGroup>
  );
};

export default CreateHourlyAbsencesForm;

const StyledFormGroup = styled.div`
  .react-datepicker__day--weekend {
    color: #d1d1d1; /* Gray color for weekends */
  }
`;

const SwitchButtons = styled.div`
  display: flex;
  gap: 10px;
`;
